import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useI18n, type StringKey } from "../i18n";
import {
  changeJob, loadContractTypes, loadDirectory, loadLeavingReasons, loadOrganisation, setManager, terminateEmployment,
  updateEmployment, updatePerson, type LeavingReason,
} from "../data";
import type { Branch, ContractType, Department, DirectoryRow, Person, Workspace } from "../types";

export type Action = "edit" | "job" | "manager" | "end";
const today = () => new Date().toISOString().slice(0, 10);

interface Props {
  ws: Workspace;
  action: Action;
  row: DirectoryRow;
  person: Person;
  onClose: () => void;
  onSaved: () => void;
}

/** One form at a time for changing an employee's record. */
export function ProfileAction({ ws, action, row, person, onClose, onSaved }: Props) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorGeneric"));
      setBusy(false);
    }
  }

  const titles: Record<Action, StringKey> = { edit: "editDetails", job: "changeJob", manager: "changeManager", end: "endEmployment" };
  const frame = (body: ReactNode, onSubmit: (e: FormEvent) => void, submitLabel: StringKey = "save", danger = false) => (
    <form className="card form-card action-card" onSubmit={onSubmit}>
      <h2>{t(titles[action])}</h2>
      {body}
      {error && <p className="alert danger" role="alert">{error}</p>}
      <div className="actions">
        <button type="button" className="btn secondary" onClick={onClose} disabled={busy}>{t("cancel")}</button>
        <button className={`btn ${danger ? "danger" : "primary"}`} disabled={busy}>{t(submitLabel)}</button>
      </div>
    </form>
  );

  if (action === "edit") return <EditDetails {...{ row, person, frame, run }} />;
  if (action === "job") return <JobChange {...{ ws, row, frame, run }} />;
  if (action === "manager") return <ManagerChange {...{ ws, row, frame, run }} />;
  return <EndEmployment {...{ row, frame, run }} />;
}

type Frame = (body: ReactNode, onSubmit: (e: FormEvent) => void, submitLabel?: StringKey, danger?: boolean) => ReactNode;
type Run = (fn: () => Promise<void>) => Promise<void>;

function useForm(initial: Record<string, string>) {
  const [f, setF] = useState(initial);
  const set = (k: string) => (e: { target: { value: string } }) => setF((p) => ({ ...p, [k]: e.target.value }));
  return { f, set };
}

function Field({ id, label, children }: { id: string; label: string; children: ReactNode }) {
  return <label className="field" htmlFor={id}><span>{label}</span>{children}</label>;
}

function EditDetails({ row, person, frame, run }: { row: DirectoryRow; person: Person; frame: Frame; run: Run }) {
  const { t } = useI18n();
  const { f, set } = useForm({
    first_name: person.first_name, last_name: person.last_name,
    first_name_ar: person.first_name_ar ?? "", last_name_ar: person.last_name_ar ?? "",
    gender: person.gender ?? "", date_of_birth: person.date_of_birth ?? "",
    national_id: person.national_id ?? "", mobile: person.mobile ?? "", personal_email: person.personal_email ?? "",
    work_email: row.work_email ?? "", probation_end_date: row.probation_end_date ?? "",
  });
  const input = (k: string, label: StringKey, type = "text", dir?: "ltr" | "rtl") => (
    <Field id={`ed-${k}`} label={t(label)}><input id={`ed-${k}`} type={type} dir={dir} value={f[k]} onChange={set(k)} /></Field>
  );
  return frame(
    <>
      <div className="field-row">{input("first_name", "firstName", "text", "ltr")}{input("last_name", "lastName", "text", "ltr")}</div>
      <div className="field-row">{input("first_name_ar", "firstNameAr", "text", "rtl")}{input("last_name_ar", "lastNameAr", "text", "rtl")}</div>
      <div className="field-row">
        <Field id="ed-gender" label={t("gender")}>
          <select id="ed-gender" value={f.gender} onChange={set("gender")}>
            <option value="">—</option><option value="female">{t("female")}</option><option value="male">{t("male")}</option>
          </select>
        </Field>
        {input("date_of_birth", "dateOfBirth", "date")}
        {person.nationality === "EG" && input("national_id", "nationalId", "text", "ltr")}
      </div>
      <div className="field-row">{input("mobile", "mobile", "tel", "ltr")}{input("personal_email", "personalEmail", "email", "ltr")}{input("work_email", "workEmail", "email", "ltr")}</div>
      <div className="field-row">{input("probation_end_date", "probationEnd", "date")}</div>
    </>,
    (e) => {
      e.preventDefault();
      const { work_email, probation_end_date, ...personFields } = f;
      void run(async () => {
        await updatePerson(person.id, personFields);
        await updateEmployment(row.employment_id, { work_email, probation_end_date });
      });
    },
  );
}

function JobChange({ ws, row, frame, run }: { ws: Workspace; row: DirectoryRow; frame: Frame; run: Run }) {
  const { t, pick } = useI18n();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [types, setTypes] = useState<ContractType[]>([]);
  const { f, set } = useForm({
    start: today(), change_reason: "promotion", job_title: row.job_title ?? "", job_title_ar: row.job_title_ar ?? "",
    department_id: "", branch_id: "", grade: row.grade ?? "", fte: row.fte != null ? String(row.fte) : "",
    contract_type: "", contract_end_date: "",
  });
  useEffect(() => {
    void Promise.all([loadOrganisation(ws.membership.company_id), loadContractTypes()]).then(([o, ct]) => {
      setDepartments(o.departments); setBranches(o.branches); setTypes(ct);
    });
  }, [ws.membership.company_id]);
  const needsEnd = types.find((c) => c.code === f.contract_type)?.needs_end_date ?? false;
  const reasons: StringKey[] = ["reason_promotion", "reason_transfer", "reason_role_change", "reason_contract_change"];
  return frame(
    <>
      <div className="field-row">
        <Field id="jc-start" label={t("effectiveFrom")}><input id="jc-start" type="date" value={f.start} onChange={set("start")} required /></Field>
        <Field id="jc-reason" label={t("reason")}>
          <select id="jc-reason" value={f.change_reason} onChange={set("change_reason")}>
            {reasons.map((r) => <option key={r} value={r.replace("reason_", "")}>{t(r)}</option>)}
          </select>
        </Field>
      </div>
      <div className="field-row">
        <Field id="jc-title" label={t("jobTitle")}><input id="jc-title" dir="ltr" value={f.job_title} onChange={set("job_title")} /></Field>
        <Field id="jc-title-ar" label={t("jobTitleAr")}><input id="jc-title-ar" dir="rtl" value={f.job_title_ar} onChange={set("job_title_ar")} /></Field>
      </div>
      <div className="field-row">
        <Field id="jc-dept" label={t("department")}>
          <select id="jc-dept" value={f.department_id} onChange={set("department_id")}>
            <option value="">{t("keepCurrent")}{row.department_name ? ` (${pick(row.department_name, row.department_name_ar)})` : ""}</option>
            {departments.filter((d) => d.id !== row.department_id).map((d) => <option key={d.id} value={d.id}>{pick(d.name, d.name_ar)}</option>)}
          </select>
        </Field>
        <Field id="jc-branch" label={t("branch")}>
          <select id="jc-branch" value={f.branch_id} onChange={set("branch_id")}>
            <option value="">{t("keepCurrent")}</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{pick(b.name, b.name_ar)}</option>)}
          </select>
        </Field>
        <Field id="jc-grade" label={t("grade")}><input id="jc-grade" value={f.grade} onChange={set("grade")} /></Field>
        <Field id="jc-fte" label={t("fte")}><input id="jc-fte" type="number" step="0.1" min="0.1" max="1" value={f.fte} onChange={set("fte")} /></Field>
      </div>
      <div className="field-row">
        <Field id="jc-contract" label={t("contractType")}>
          <select id="jc-contract" value={f.contract_type} onChange={set("contract_type")}>
            <option value="">{t("keepCurrent")}</option>
            {types.filter((c) => c.code !== row.contract_type_code).map((c) => <option key={c.code} value={c.code}>{pick(c.name_en, c.name_ar)}</option>)}
          </select>
        </Field>
        {needsEnd && <Field id="jc-end" label={t("contractEnd")}><input id="jc-end" type="date" value={f.contract_end_date} onChange={set("contract_end_date")} required /></Field>}
      </div>
    </>,
    (e) => {
      e.preventDefault();
      const { start, ...fields } = f;
      void run(() => changeJob(row.employment_id, start, fields));
    },
  );
}

function ManagerChange({ ws, row, frame, run }: { ws: Workspace; row: DirectoryRow; frame: Frame; run: Run }) {
  const { t, pick } = useI18n();
  const [people, setPeople] = useState<DirectoryRow[]>([]);
  const { f, set } = useForm({ start: today(), manager: "" });
  useEffect(() => {
    void loadDirectory(ws.membership.company_id).then((all) =>
      setPeople(all.filter((p) => p.employment_id !== row.employment_id && p.status !== "terminated")));
  }, [ws.membership.company_id, row.employment_id]);
  return frame(
    <div className="field-row">
      <Field id="mc-manager" label={t("manager")}>
        <select id="mc-manager" value={f.manager} onChange={set("manager")}>
          <option value="">{t("noManager")}</option>
          {people.map((p) => (
            <option key={p.employment_id} value={p.employment_id}>
              {pick(p.full_name, p.full_name_ar)} · {pick(p.job_title ?? "", p.job_title_ar)}
            </option>
          ))}
        </select>
      </Field>
      <Field id="mc-start" label={t("effectiveFrom")}><input id="mc-start" type="date" value={f.start} onChange={set("start")} required /></Field>
    </div>,
    (e) => {
      e.preventDefault();
      void run(() => setManager(row.employment_id, f.manager || null, f.start));
    },
  );
}

function EndEmployment({ row, frame, run }: { row: DirectoryRow; frame: Frame; run: Run }) {
  const { t, pick } = useI18n();
  const [reasons, setReasons] = useState<LeavingReason[]>([]);
  const { f, set } = useForm({ last_day: today(), reason: "" });
  const [regrettable, setRegrettable] = useState(false);
  const [rehire, setRehire] = useState(true);
  useEffect(() => { void loadLeavingReasons().then(setReasons); }, []);
  return frame(
    <>
      <p className="muted">{t("endEmploymentHelp")}</p>
      <div className="field-row">
        <Field id="te-day" label={t("lastDay")}><input id="te-day" type="date" min={row.hire_date} value={f.last_day} onChange={set("last_day")} required /></Field>
        <Field id="te-reason" label={t("leavingReason")}>
          <select id="te-reason" value={f.reason} onChange={set("reason")} required>
            <option value="">—</option>
            {reasons.map((r) => <option key={r.code} value={r.code}>{pick(r.name_en, r.name_ar)}</option>)}
          </select>
        </Field>
      </div>
      <label className="check"><input id="te-regret" type="checkbox" checked={regrettable} onChange={(e) => setRegrettable(e.target.checked)} /> {t("regrettable")}</label>
      <label className="check"><input id="te-rehire" type="checkbox" checked={rehire} onChange={(e) => setRehire(e.target.checked)} /> {t("rehireEligible")}</label>
    </>,
    (e) => {
      e.preventDefault();
      if (!f.reason) return;
      void run(() => terminateEmployment(row.employment_id, f.last_day, f.reason, regrettable, rehire));
    },
    "confirmEnd",
    true,
  );
}
